import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  STORAGE_KEY,
  hasResponded,
  loadConsent,
  saveConsent,
  onConsentChange,
} from '@/lib/cookieConsent';

/**
 * CookieConsent
 * -------------
 * GDPR-compliant cookie consent banner for APEX Verity.
 *
 * Behaviour:
 *  • First visit (no stored decision): banner appears at the bottom.
 *  • Accept All → all categories on, dismiss, persisted 12 months.
 *  • Reject Non-Essential → only essential on, dismiss, persisted 12 months.
 *  • Manage Preferences → expands to show per-category toggles + Save.
 *  • Subsequent visits: hidden unless the user opens it via the footer
 *    "Cookie Preferences" link (which dispatches the `apex:cookie-prefs-open`
 *    custom event listened to below).
 *
 * Compliance:
 *  • Pre-checked boxes only for ESSENTIAL (regulatorily exempt).
 *  • Analytics + Preferences toggles default to OFF until explicitly enabled.
 *  • No tracking is loaded by this component — it only writes the consent
 *    record; the gtag-loading logic lives in lib/cookieConsent.js and is
 *    invoked from App.js on every consent change.
 */

const OPEN_EVENT = 'apex:cookie-prefs-open';

// Visual constants matched to the existing dark theme.
const C = {
  bg: '#0B0E16',
  bgPanel: '#10131C',
  border: '#14171F',
  borderStrong: '#1E2A45',
  text: '#F1F5F9',
  textMid: '#94A3B8',
  textDim: '#475569',
  accent: '#0D9488',
  fontMono: 'JetBrains Mono, ui-monospace, monospace',
  fontBody: 'Inter, system-ui, sans-serif',
};

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, preferences: false });
  const firstFocusRef = useRef(null);

  // First-mount: show banner only if no valid decision exists yet.
  useEffect(() => {
    if (!hasResponded()) {
      setOpen(true);
    }
    // Always sync the toggle state from storage so re-opening from the
    // footer pre-fills with the user's last choice.
    const existing = loadConsent();
    if (existing) {
      setPrefs({
        analytics: Boolean(existing.analytics),
        preferences: Boolean(existing.preferences),
      });
    }
  }, []);

  // Footer "Cookie Preferences" link dispatches this event.
  useEffect(() => {
    const openHandler = () => {
      const existing = loadConsent();
      if (existing) {
        setPrefs({
          analytics: Boolean(existing.analytics),
          preferences: Boolean(existing.preferences),
        });
      }
      setExpanded(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, openHandler);
    return () => window.removeEventListener(OPEN_EVENT, openHandler);
  }, []);

  // Also react to external mutations to the storage key (e.g. another tab
  // making a choice) so the banner closes appropriately.
  useEffect(() => {
    const unsub = onConsentChange(() => {
      if (hasResponded()) setOpen(false);
    });
    const storageHandler = (e) => {
      if (e.key === STORAGE_KEY && hasResponded()) setOpen(false);
    };
    window.addEventListener('storage', storageHandler);
    return () => {
      unsub();
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  // Focus management: when the banner opens, focus the most prominent action.
  useEffect(() => {
    if (open && firstFocusRef.current) {
      const t = setTimeout(() => {
        try { firstFocusRef.current.focus(); } catch (_) { /* no-op */ }
      }, 120);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  if (!open) return null;

  function acceptAll() {
    saveConsent({ analytics: true, preferences: true });
    setOpen(false);
    setExpanded(false);
  }

  function rejectNonEssential() {
    saveConsent({ analytics: false, preferences: false });
    setOpen(false);
    setExpanded(false);
  }

  function savePreferences() {
    saveConsent({ analytics: prefs.analytics, preferences: prefs.preferences });
    setOpen(false);
    setExpanded(false);
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="apex-cookie-title"
      aria-describedby="apex-cookie-desc"
      data-testid="cookie-consent-banner"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        background: C.bg,
        borderTop: `1px solid ${C.border}`,
        boxShadow: '0 -12px 40px rgba(0,0,0,0.45)',
        color: C.text,
        fontFamily: C.fontBody,
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '20px 24px 22px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div
              id="apex-cookie-title"
              style={{
                fontFamily: C.fontMono,
                fontSize: 10,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: C.textDim,
                marginBottom: 6,
              }}
            >
              APEX Verity · Cookie Settings
            </div>
            <p
              id="apex-cookie-desc"
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: C.textMid,
                maxWidth: 760,
              }}
            >
              We use cookies for essential site functionality and, with your permission,
              for analytics and preferences. Essential cookies are always on. You can
              accept all, reject non-essential, or choose categories. See our{' '}
              <Link
                to="/privacy"
                style={{ color: C.text, textDecoration: 'underline', textUnderlineOffset: 3 }}
                data-testid="cookie-privacy-link"
              >
                Privacy Policy
              </Link>{' '}
              for details.
            </p>
          </div>
        </div>

        {/* Expanded preferences */}
        {expanded && (
          <div
            data-testid="cookie-consent-prefs"
            style={{
              border: `1px solid ${C.border}`,
              background: C.bgPanel,
              padding: 16,
              display: 'grid',
              gap: 12,
            }}
          >
            <CategoryRow
              testid="cookie-cat-essential"
              label="Essential"
              copy="Required for the site to function: session, authentication, security, load balancing. Cannot be disabled."
              alwaysOn
            />
            <CategoryRow
              testid="cookie-cat-analytics"
              label="Analytics"
              copy="Helps us understand which pages are useful via Google Analytics. Sets gtag cookies only after you opt in here."
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
            />
            <CategoryRow
              testid="cookie-cat-preferences"
              label="Preferences"
              copy="Remembers your UI choices: theme, layout, dismissed banners. No tracking; data stays in your browser."
              checked={prefs.preferences}
              onChange={(v) => setPrefs((p) => ({ ...p, preferences: v }))}
            />
          </div>
        )}

        {/* Action row */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {!expanded && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              data-testid="cookie-manage-btn"
              style={ghostBtn()}
            >
              Manage Preferences
            </button>
          )}
          <button
            type="button"
            onClick={rejectNonEssential}
            data-testid="cookie-reject-btn"
            style={ghostBtn()}
          >
            Reject Non-Essential
          </button>
          {expanded ? (
            <button
              type="button"
              ref={firstFocusRef}
              onClick={savePreferences}
              data-testid="cookie-save-btn"
              style={primaryBtn()}
            >
              Save Preferences
            </button>
          ) : (
            <button
              type="button"
              ref={firstFocusRef}
              onClick={acceptAll}
              data-testid="cookie-accept-btn"
              style={primaryBtn()}
            >
              Accept All
            </button>
          )}
        </div>
      </div>

      {/* Mobile-friendly tweaks — buttons stack full-width on narrow screens */}
      <style>{`
        @media (max-width: 560px) {
          [data-testid="cookie-consent-banner"] [data-testid$="-btn"] {
            width: 100%;
          }
          [data-testid="cookie-consent-banner"] > div > div:last-child {
            flex-direction: column;
            align-items: stretch !important;
          }
        }
      `}</style>
    </div>
  );
}

// ─── helpers ───────────────────────────────────────────────────────────────

function CategoryRow({ label, copy, checked, onChange, alwaysOn, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 16,
        alignItems: 'center',
        paddingBottom: 8,
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <div>
        <div style={{
          fontFamily: C.fontMono,
          fontSize: 11,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: C.text,
        }}>
          {label}
          {alwaysOn && (
            <span style={{ marginLeft: 10, color: C.accent, fontSize: 10 }}>· always on</span>
          )}
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 12.5, color: C.textMid, lineHeight: 1.55 }}>{copy}</p>
      </div>

      {alwaysOn ? (
        <div
          aria-disabled="true"
          style={{
            width: 40, height: 22, borderRadius: 11,
            background: 'rgba(13,148,136,0.35)',
            position: 'relative',
            cursor: 'not-allowed',
            opacity: 0.7,
          }}
        >
          <span style={{
            position: 'absolute', top: 3, right: 3,
            width: 16, height: 16, borderRadius: '50%',
            background: '#E2E8F0',
          }} />
        </div>
      ) : (
        <Toggle checked={checked} onChange={onChange} testid={`${testid}-toggle`} />
      )}
    </div>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testid}
      style={{
        width: 40, height: 22, borderRadius: 11,
        background: checked ? C.accent : '#1A1F2E',
        border: `1px solid ${checked ? C.accent : C.borderStrong}`,
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.2s ease, border-color 0.2s ease',
        padding: 0,
        flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 2,
        left: checked ? 20 : 2,
        width: 16, height: 16, borderRadius: '50%',
        background: '#E2E8F0',
        transition: 'left 0.2s ease',
      }} />
    </button>
  );
}

function primaryBtn() {
  return {
    background: C.accent,
    color: C.text,
    border: `1px solid ${C.accent}`,
    padding: '11px 22px',
    borderRadius: 2,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontFamily: C.fontBody,
    transition: 'opacity 0.2s ease',
  };
}

function ghostBtn() {
  return {
    background: 'transparent',
    color: C.text,
    border: `1px solid rgba(226,232,240,0.25)`,
    padding: '11px 22px',
    borderRadius: 2,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontFamily: C.fontBody,
    transition: 'background 0.2s ease, border-color 0.2s ease',
  };
}

/**
 * Dispatch this event from anywhere to re-open the consent banner with the
 * expanded preferences panel. Used by the LandingFooter "Cookie Preferences"
 * link.
 */
export function openCookiePreferences() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}
